/* eslint-disable max-lines-per-function,max-lines */

import { useCallback } from 'react';

import { AppointmentStatus } from '@dreryk/egabinet-appointment-frontend-api';
import { VisitStatus } from '@dreryk/egabinet-visit-frontend-api';

import { createTranslatedText, phoneNumberFormatter } from '@dreryk/react-common';
import {
   ContextDialog,
   FluidTab,
   FluidTabs,
   Link,
   LinkSize,
   Notification,
   NotificationSize,
   NotificationType,
   StethoscopeIcon,
   StyledFlex,
   TelephoneIcon,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { isAppointmentCancelled } from '../../../../../common/common.utils';
import receptionService from '../../../../../receptionService';
import { calendarTranslationKeys } from '../../../../../translations';

import { AppointmentAdvancePaymentInfoItem } from './AppointmentAdvancePayments/AppointmentAdvancePaymentInfoItem';
import { AppointmentDetailsCancelButton } from './AppointmentDetailsCancelButton';
import { AppointmentDetailsConfirmButton } from './AppointmentDetailsConfirmButton';
import { AppointmentDetailsInfoItem } from './AppointmentDetailsInfoItem';
import { AppointmentDetailsAppointmentSourceLabelIcon } from './AppointmentDetailsAppointmentSourceLabelIcon';
import { AppointmentEncounterModeInfoItem } from './encounterMode/AppointmentEncounterModeInfoItem';
import { AppointmentConfirmationStatusInfoItem } from './appointmentConfirmationStatus/AppointmentConfirmationStatusInfoItem';
import { AppointmentUnknownPatientInfoItem } from './unknownPatient/AppointmentUnknownPatientInfoItem';
import { AppointmentCancellationInfoItem } from './cancellationInfo/AppointmentCancellationInfoItem';
import { PatientPresenceStatusInfoItem } from './patientPresenceStatus/PatientPresenceStatusInfoItem';
import { AppointmentNoteInfoItem } from './appointmentNote/AppointmentNoteInfoItem';
import { AppointmentCreatedByInfoItem } from './appointmentCreatedBy/AppointmentCreatedByInfoItem';
import { AppointmentDetailsFluidTabLoader } from './useAppointmentDetailsFluidTab.loader';
import { getPractitionerName } from './useAppointmentDetailsFluidTab.utils';
import { AppointmentAdvancePaymentLinks } from './AppointmentAdvancePayments/AppointmentAdvancePaymentLinks';
import { AppointmentVisitSettlementInfoItem } from './visitSettlement/AppointmentVisitSettlementInfoItem';

const translationKeys = calendarTranslationKeys.appointmentContextDialog.tabs.appointmentDetailsTab;

type UseAppointmentDetailsFluidTabProps = {
   patientId: number;
   appointmentId: number;
   beginDate: Date;
   endDate: Date;
   visitId: number | undefined;
   onVisitAlreadyStartedFault: VoidFunction;
   onAppointmentAlreadyCancelledFault: VoidFunction;
   appointmentStatus: AppointmentStatus;
   visitStatus: VisitStatus | undefined;
   onAppointmentCancelled: VoidFunction;
   onAppointmentUpdated: VoidFunction;
   onClose: VoidFunction;
   timeSlotConfirmed: boolean;
};

export const useAppointmentDetailsFluidTab = (props: UseAppointmentDetailsFluidTabProps): FluidTab => {
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const handleAppointmentCancelled = useCallback((): void => {
      props.onAppointmentCancelled();
      props.onClose();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.onAppointmentCancelled, props.onClose]);

   return (
      <FluidTabs.Tab id={'appointmentDetails'} testId={'appointmentDetails'} label={translationKeys.label}>
         <AppointmentDetailsFluidTabLoader appointmentId={props.appointmentId}>
            {({
               appointmentDetails,
               receptionServices,
               visitSettlement,
               appointmentAdvancePayment,
               onlinePaymentsIntegrationExists,
               refetchReceptionServices,
               refetchAppointmentDetails,
               refetchVisitSettlement,
               refetchAdvancePayment,
            }) => {
               const handleAppointmentUpdated = (): void => {
                  props.onAppointmentUpdated();
                  refetchReceptionServices();
               };

               const handleRefundSuccess = (): void => {
                  refetchVisitSettlement();
                  refetchAdvancePayment();
               };

               return (
                  <>
                     <StyledFlex direction={'column'} gap={'12px'}>
                        {appointmentDetails.timeSlotConfirmed ? null : (
                           <Notification
                              testId={'timeSlotNotConfirmed'}
                              type={NotificationType.WARNING}
                              label={translationKeys.timeSlotNotConfirmedWarning}
                              size={NotificationSize.SMALLER}
                           />
                        )}
                        <AppointmentUnknownPatientInfoItem
                           patientReferenceIdentifier={appointmentDetails.patient.referenceIdentifier}
                        />
                        <AppointmentDetailsInfoItem
                           testId={'patientContact'}
                           icon={<TelephoneIcon />}
                           disabled={!appointmentDetails.patient.phoneNumber}
                           content={
                              appointmentDetails.patient.phoneNumber ? (
                                 <Link
                                    testId={'patientContact'}
                                    label={createTranslatedText(
                                       phoneNumberFormatter(appointmentDetails.patient.phoneNumber)
                                    )}
                                    href={`tel:${appointmentDetails.patient.phoneNumber.asString()}`}
                                    size={LinkSize.NORMAL}
                                 />
                              ) : (
                                 translationKeys.noContactToPatient
                              )
                           }
                           description={translationKeys.contactToPatient}
                        />
                        <AppointmentDetailsInfoItem
                           testId={'practitioner'}
                           icon={<StethoscopeIcon />}
                           content={getPractitionerName(appointmentDetails.practitioner)}
                           description={translationKeys.visitConductor}
                        />
                        <AppointmentCreatedByInfoItem
                           createdBy={appointmentDetails.createdBy}
                           creationDate={appointmentDetails.creationDate}
                        />
                        <AppointmentDetailsAppointmentSourceLabelIcon
                           sourceLabel={appointmentDetails.sourceLabel}
                           creationDate={appointmentDetails.creationDate}
                        />
                        <AppointmentEncounterModeInfoItem
                           appointmentId={props.appointmentId}
                           encounterMode={appointmentDetails.encounterMode}
                           appointmentStatus={props.appointmentStatus}
                           visitStatus={props.visitStatus}
                        />
                        {appointmentDetails.timeSlotConfirmed ? (
                           <>
                              <AppointmentConfirmationStatusInfoItem
                                 confirmed={appointmentDetails.presenceConfirmed}
                                 appointmentId={props.appointmentId}
                                 appointmentStatus={props.appointmentStatus}
                              />
                              <PatientPresenceStatusInfoItem
                                 appointmentId={props.appointmentId}
                                 patientPresence={appointmentDetails.patientPresence}
                              />
                           </>
                        ) : null}
                        <AppointmentNoteInfoItem
                           note={appointmentDetails.note}
                           appointmentId={appointmentDetails.id}
                           onAppointmentNoteUpdated={refetchAppointmentDetails}
                        />
                        <>
                           {privateMedicalServicesReadOnlyAvailable ? (
                              <AppointmentAdvancePaymentInfoItem
                                 appointmentAdvancePayment={appointmentAdvancePayment}
                                 onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
                              />
                           ) : (
                              <>
                                 <receptionService.calendar.appointment.ReceptionServiceList
                                    receptionServices={receptionServices}
                                 />
                                 <AppointmentVisitSettlementInfoItem
                                    appointmentId={props.appointmentId}
                                    visitSettlement={visitSettlement}
                                 />
                                 <AppointmentAdvancePaymentInfoItem
                                    appointmentAdvancePayment={appointmentAdvancePayment}
                                    onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
                                 />
                                 {visitSettlement ? (
                                    <AppointmentAdvancePaymentLinks
                                       appointmentId={props.appointmentId}
                                       advancePayments={visitSettlement.advancePayments}
                                       beginDate={appointmentDetails.beginDate}
                                       endDate={appointmentDetails.endDate}
                                       medicalServices={visitSettlement.medicalServices}
                                       patientFirstName={appointmentDetails.patient.firstName?.asString() ?? null}
                                       patientLastName={appointmentDetails.patient.lastName?.asString() ?? null}
                                       onRefundSuccess={handleRefundSuccess}
                                       appointmentAdvancePayment={appointmentAdvancePayment}
                                       onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
                                    />
                                 ) : null}
                              </>
                           )}
                        </>

                        {appointmentDetails.cancellationDetails ? (
                           <AppointmentCancellationInfoItem
                              cancellationDetails={appointmentDetails.cancellationDetails}
                           />
                        ) : null}
                     </StyledFlex>

                     {isAppointmentCancelled(props.appointmentStatus)
                        ? null
                        : protectFeature(
                             'creatingAppointments',
                             <AppointmentDetailsCancelButton
                                onAppointmentCancelled={handleAppointmentCancelled}
                                appointmentId={props.appointmentId}
                                appointmentStatus={props.appointmentStatus}
                                timeSlotConfirmed={props.timeSlotConfirmed}
                             />
                          )}
                     <AppointmentDetailsConfirmButton
                        appointmentId={props.appointmentId}
                        beginDate={props.beginDate}
                        patientId={props.patientId}
                        appointmentStatus={props.appointmentStatus}
                        visitStatus={props.visitStatus}
                        visitId={props.visitId}
                        onVisitAlreadyStartedFault={props.onVisitAlreadyStartedFault}
                        onAppointmentAlreadyCancelledFault={props.onAppointmentAlreadyCancelledFault}
                        ownedByCurrentUser={appointmentDetails.ownedByCurrentUser}
                        onAppointmentUpdated={handleAppointmentUpdated}
                        timeSlotConfirmed={appointmentDetails.timeSlotConfirmed}
                        sourceLabel={appointmentDetails.sourceLabel}
                     />

                     <ContextDialog.CloseButton onClick={props.onClose} />
                  </>
               );
            }}
         </AppointmentDetailsFluidTabLoader>
      </FluidTabs.Tab>
   );
};
